import React, { useState } from 'react';
import type { ProactiveSuggestion, CanvasAction } from '@/services/api/agentApi';

interface ProactiveSuggestionPanelProps {
  suggestions: ProactiveSuggestion[];
  onApply: (actions: CanvasAction[]) => void;
  onDismiss: (suggestionId: string) => void;
  onFeedback: (suggestionId: string, action: 'helpful' | 'unhelpful') => void;
  className?: string;
}

const severityConfig = {
  critical: { bg: '#FEF2F2', border: '#FECACA', text: '#991B1B', badge: '#EF4444' },
  warning: { bg: '#FFFBEB', border: '#FDE68A', text: '#92400E', badge: '#F59E0B' },
  info: { bg: '#EFF6FF', border: '#BFDBFE', text: '#1E40AF', badge: '#3B82F6' },
};

const categoryIcons: Record<string, string> = {
  accessibility: 'A11Y',
  'brand-compliance': 'Brand',
  layout: 'Layout',
  'data-opportunity': 'Data',
  performance: 'Perf',
  ux: 'UX',
};

export const ProactiveSuggestionPanel: React.FC<ProactiveSuggestionPanelProps> = ({
  suggestions,
  onApply,
  onDismiss,
  onFeedback,
  className = '',
}) => {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set());

  const visibleSuggestions = suggestions.filter(s => !dismissedIds.has(s.id));

  if (visibleSuggestions.length === 0) return null;

  const toggleExpanded = (id: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleDismiss = (id: string) => {
    setDismissedIds(prev => new Set(prev).add(id));
    onDismiss(id);
  };

  return (
    <div
      className={className}
      style={{
        border: '1px solid #E5E7EB',
        borderRadius: '8px',
        overflow: 'hidden',
        fontSize: '13px',
      }}
    >
      <div
        style={{
          padding: '8px 12px',
          background: '#F9FAFB',
          fontWeight: 500,
          color: '#374151',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <span>Design Suggestions ({visibleSuggestions.length})</span>
      </div>

      <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
        {visibleSuggestions.map(suggestion => {
          const config = severityConfig[suggestion.severity];
          const isExpanded = expandedIds.has(suggestion.id);

          return (
            <div
              key={suggestion.id}
              style={{
                padding: '10px 12px',
                borderBottom: '1px solid #F3F4F6',
                background: config.bg,
                borderLeft: `3px solid ${config.badge}`,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                <span
                  style={{
                    background: config.badge + '20',
                    color: config.badge,
                    padding: '1px 6px',
                    borderRadius: '3px',
                    fontSize: '10px',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {categoryIcons[suggestion.category] || suggestion.category}
                </span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 500, color: config.text }}>{suggestion.title}</div>
                  <div style={{ color: '#6B7280', marginTop: '2px', lineHeight: 1.4 }}>
                    {suggestion.description}
                  </div>
                  {suggestion.wcagCriteria && (
                    <span
                      style={{
                        display: 'inline-block',
                        marginTop: '4px',
                        background: '#E0E7FF',
                        color: '#4338CA',
                        padding: '1px 5px',
                        borderRadius: '3px',
                        fontSize: '10px',
                      }}
                    >
                      WCAG {suggestion.wcagCriteria}
                    </span>
                  )}

                  {isExpanded && suggestion.reasoning && (
                    <div
                      style={{
                        marginTop: '6px',
                        padding: '6px 8px',
                        background: 'rgba(255,255,255,0.6)',
                        borderRadius: '4px',
                        fontSize: '12px',
                        color: '#4B5563',
                      }}
                    >
                      {suggestion.reasoning}
                    </div>
                  )}
                </div>
              </div>

              <div style={{ display: 'flex', gap: '6px', marginTop: '8px', justifyContent: 'flex-end' }}>
                {suggestion.reasoning && (
                  <button
                    onClick={() => toggleExpanded(suggestion.id)}
                    style={{
                      padding: '3px 8px',
                      border: '1px solid #D1D5DB',
                      borderRadius: '4px',
                      background: 'white',
                      cursor: 'pointer',
                      fontSize: '11px',
                      color: '#6B7280',
                    }}
                  >
                    {isExpanded ? 'Less' : 'Why?'}
                  </button>
                )}
                {suggestion.suggestedActions && suggestion.suggestedActions.length > 0 && (
                  <button
                    onClick={() => onApply(suggestion.suggestedActions!)}
                    style={{
                      padding: '3px 8px',
                      border: 'none',
                      borderRadius: '4px',
                      background: '#10B981',
                      color: 'white',
                      cursor: 'pointer',
                      fontSize: '11px',
                      fontWeight: 500,
                    }}
                  >
                    Auto-fix
                  </button>
                )}
                <button
                  onClick={() => onFeedback(suggestion.id, 'helpful')}
                  style={{
                    padding: '3px 6px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '4px',
                    background: 'white',
                    cursor: 'pointer',
                    fontSize: '11px',
                  }}
                  title="Helpful"
                >
                  +
                </button>
                <button
                  onClick={() => handleDismiss(suggestion.id)}
                  style={{
                    padding: '3px 6px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '4px',
                    background: 'white',
                    cursor: 'pointer',
                    fontSize: '11px',
                    color: '#9CA3AF',
                  }}
                  title="Dismiss"
                >
                  x
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
